import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';

interface DeleteConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  message?: string;
  itemCount?: number;
  itemName?: string;
  isLoading?: boolean;
  loadingText?: string;
  confirmText?: string;
  cancelText?: string;
}

const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({
  open,
  onClose,
  onConfirm,
  title = "Xác nhận xóa",
  message,
  itemCount = 0,
  itemName = "mục",
  isLoading = false,
  loadingText = "Đang xóa...",
  confirmText = "Xóa",
  cancelText = "Hủy",
}) => {
  const displayMessage = message || `Bạn có chắc chắn muốn xóa ${itemCount} ${itemName} đã chọn không?`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="delete-dialog-title"
      aria-describedby="delete-dialog-description"
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        }
      }}
    >
      <DialogTitle 
        id="delete-dialog-title"
        sx={{
          pb: 1,
          fontSize: '1.25rem',
          fontWeight: 600,
          color: '#1f2937',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}
      >
       
        {title}
      </DialogTitle>
      
      <DialogContent sx={{ pt: 2, pb: 3 }}>
        <DialogContentText 
          id="delete-dialog-description"
          sx={{
            fontSize: '1rem',
            color: '#374151',
            lineHeight: 1.6,
            mb: 2
          }}
        >
          {displayMessage}
        </DialogContentText>
        
        <Box
          sx={{
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: 2,
            p: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <Box
            sx={{
              width: 20,
              height: 20,
              borderRadius: '50%',
              backgroundColor: '#fca5a5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#dc2626',
              fontSize: '0.75rem'
            }}
          >
            ⚠️
          </Box>
          <Box sx={{ fontSize: '0.875rem', color: '#dc2626', fontWeight: 500 }}>
            Hành động này không thể hoàn tác!
          </Box>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 3, pt: 0, gap: 1 }}>
        <Button 
          onClick={onClose} 
          disabled={isLoading}
          variant="outlined"
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 500,
            px: 3,
            py: 1,
            borderColor: '#d1d5db',
            color: '#374151',
            '&:hover': {
              borderColor: '#9ca3af',
              backgroundColor: '#f9fafb'
            }
          }}
        >
          {cancelText}
        </Button>
        
        <Button 
          onClick={onConfirm} 
          disabled={isLoading}
          variant="contained"
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            py: 1,
            backgroundColor: '#dc2626',
            boxShadow: '0 1px 3px 0 rgba(220, 38, 38, 0.3)',
            '&:hover': {
              backgroundColor: '#b91c1c',
              boxShadow: '0 4px 6px -1px rgba(220, 38, 38, 0.3)'
            },
            '&:disabled': {
              backgroundColor: '#fca5a5',
              color: '#ffffff'
            }
          }}
          autoFocus
        >
          {isLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  border: '2px solid #ffffff',
                  borderTop: '2px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' }
                  }
                }}
              />
              {loadingText}
            </Box>
          ) : (
            confirmText
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteConfirmationModal;
